/*
  home-reviews-live.js
  WHAT: Replaces the homepage review cards with the most RECENT reviews pulled live
        from Yotpo across several products, newest first.
  WHY:  Owner 2026-10-21: "the reviews should change over time and be the most recent
        ones." The curated `custom.review_highlights` cards are hand-picked and go stale
        the moment a new review lands.
  WHEN: Added 2026-10-21.

  This is the homepage sibling of `pdp-reviews-live.js` and works the same way, with one
  real difference: Yotpo's widget API is PER PRODUCT, so this fetches each source product
  separately, merges the responses, and sorts the merged list by date. Sorting only within
  each product would have produced a row that is newest-per-product but not newest overall.

  PROGRESSIVE ENHANCEMENT - deliberate, and the reason this is safe:
    - Liquid renders the curated cards server-side. That is the first paint, it works with
      JS off, and it is what crawlers and answer engines index.
    - This script then swaps in the live ones.
    - Any failure - offline, CORS change, rate limit, empty response, one product 404ing -
      leaves the server-rendered cards exactly as they are. The band can never end up empty
      because of this file. That matters: an empty review band on a phone is the precise
      failure we just spent a day removing.

  NOT THE YOTPO WIDGET. This is a plain `fetch` plus our own DOM, not Yotpo's carousel
  bundle. The carousel is what failed to render on the owner's iPhone; nothing here depends
  on it, and nothing here can reproduce that failure mode.

  FEASIBILITY (verified 2026-08-21 for the PDP, same endpoint): the Yotpo widget API returns
  `Access-Control-Allow-Origin: https://blueant.com.au`, so a browser fetch from the
  storefront is permitted. The app key is a PUBLIC widget key - the same one Yotpo's own
  on-site widget exposes - so there is no secret in the theme.
*/
(function () {
  'use strict';

  // Strip emoji: the design system bans them in product copy.
  var EMOJI = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{2B00}-\u{2BFF}]/gu;

  function clean(s) {
    return String(s || '').replace(/\s+/g, ' ').replace(EMOJI, '').trim();
  }

  /* U+FFFD repair, same reasoning as pdp-reviews-live.js: in practice the offender is almost
     always a single mangled apostrophe inside a contraction ("don�t"), and discarding a whole
     well-written review over one bad byte loses real content. Between two letters it is
     repaired to an apostrophe; anywhere else it is dropped. This is a transcoding fix, not an
     edit to the customer's words, so design rule 10 ("real reviews unedited") holds. */
  function repair(s) {
    return String(s).replace(/([A-Za-z])�([A-Za-z])/g, '$1’$2').replace(/�/g, '');
  }

  function isBroken(s) { return String(s).indexOf('�') !== -1; }

  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function relative(iso) {
    if (!iso) return '';
    var then = new Date(iso).getTime();
    if (!then) return '';
    var days = Math.floor((Date.now() - then) / 86400000);
    if (days <= 1) return 'today';
    if (days < 7) return days + ' days ago';
    if (days < 60) return Math.floor(days / 7) + ' weeks ago';
    if (days < 730) return Math.floor(days / 30) + ' months ago';
    return Math.floor(days / 365) + ' years ago';
  }

  function starRow(n) {
    var out = '';
    for (var i = 1; i <= 5; i++) out += (i <= n ? '★' : '☆');
    return out;
  }

  /* Builds a card with the SAME class names Liquid emits, so every existing style - the
     scroller, the amber stars, the equal-height cards - keeps working untouched. */
  function card(r) {
    var el = document.createElement('li');
    el.className = 'ba-home-reviews__card';
    el.setAttribute('data-review-source', 'yotpo');
    /* Yotpo review titles are whatever the customer typed, and a good number of them are just
       the product name ("X5i", "headphone"). Sitting directly above the product link, that
       reads as the same word printed twice. Drop the headline when it is only the product
       name; the quote below it carries the card either way. */
    var slug = function (s) { return String(s).toLowerCase().replace(/[^a-z0-9]/g, ''); };
    var echoesProduct = r.title && slug(r.title) === slug(r.product);
    var head = r.title && !echoesProduct
      ? '<p class="ba-home-reviews__card-headline">' + esc(r.title) + '</p>'
      : '';
    var who = r.name
      ? '<span class="ba-home-reviews__reviewer">' + esc(r.name) + '</span>'
      : '';
    /* The date appears only on LIVE cards. It is the visible proof that the row is current,
       which is the whole point of this file; the curated fallback cards carry no date because
       the metafield does not record one, and inventing one would be fabrication. */
    var when = r.when
      ? '<span class="ba-home-reviews__when">' + esc(r.when) + '</span>'
      : '';
    el.innerHTML =
      '<div class="ba-home-reviews__stars" role="img" aria-label="' + r.score +
        ' out of 5 stars"><span aria-hidden="true">' + starRow(r.score) + '</span></div>' +
      head +
      '<blockquote class="ba-home-reviews__quote">' + esc(r.body) + '</blockquote>' +
      '<p class="ba-home-reviews__meta">' + who + when +
        '<a class="ba-home-reviews__product" href="' + esc(r.url) + '">' + esc(r.product) + '</a>' +
      '</p>';
    return el;
  }

  function fetchProduct(key, p) {
    // sort=date&direction=desc so the per-product cap really is "the most recent".
    var url = 'https://api-cdn.yotpo.com/v1/widget/' + encodeURIComponent(key) +
              '/products/' + encodeURIComponent(p.id) +
              '/reviews.json?per_page=40&page=1&sort=date&direction=desc';
    return fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var list = (data && data.response && data.response.reviews) || [];
        return list.map(function (v) {
          return {
            score: Number(v.score) || 5,
            title: repair(clean(v.title)),
            body: repair(clean(v.content)),
            name: repair(clean(v.user && v.user.display_name)),
            at: new Date(v.created_at).getTime() || 0,
            when: relative(v.created_at),
            product: p.title,
            url: p.url
          };
        });
      })
      // One product failing must not lose the other four.
      .catch(function () { return []; });
  }

  function init(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('[data-section-type="home-reviews"][data-yotpo-key]');

    Array.prototype.forEach.call(sections, function (section) {
      var key = section.getAttribute('data-yotpo-key');
      var grid = section.querySelector('.ba-home-reviews__grid');
      if (!key || !grid) return;

      var max = parseInt(section.getAttribute('data-max') || '25', 10);
      var minLen = 2;

      /* Phrases the owner has asked never to appear. Owner 2026-10-21: "we also did not want
         some of the reviews to be used." Removing a review from the curated metafield does
         NOT keep it out of this live feed, so the blocklist has to be applied here too -
         otherwise an excluded review walks straight back in the moment the swap happens. */
      var blocked = [];
      try {
        blocked = JSON.parse(section.getAttribute('data-exclude') || '[]');
      } catch (e) { blocked = []; }

      var products = [];
      try {
        products = JSON.parse(section.getAttribute('data-products') || '[]');
      } catch (e) { return; }
      if (!products.length) return;

      Promise.all(products.map(function (p) { return fetchProduct(key, p); }))
        .then(function (groups) {
          var all = [];
          groups.forEach(function (g) { all = all.concat(g); });

          // THE POINT OF THE FILE: one sort across every product, newest first.
          all.sort(function (a, b) { return b.at - a.at; });

          var out = [];
          var seen = {};
          for (var i = 0; i < all.length && out.length < max; i++) {
            var r = all[i];
            if (!r.name || r.body.length < minLen) continue;
            if (isBroken(r.body) || isBroken(r.name) || isBroken(r.title)) continue;

            var hay = (r.title + ' ' + r.body).toLowerCase();
            var skip = false;
            for (var j = 0; j < blocked.length; j++) {
              if (blocked[j] && hay.indexOf(String(blocked[j]).toLowerCase()) !== -1) {
                skip = true; break;
              }
            }
            if (skip) continue;

            // Yotpo can return near-duplicates across pages.
            var k = r.body.slice(0, 40).toLowerCase();
            if (seen[k]) continue;
            seen[k] = 1;
            out.push(r);
          }

          // Nothing usable came back: keep the server-rendered cards rather than blanking.
          if (!out.length) return;

          var frag = document.createDocumentFragment();
          out.forEach(function (r) { frag.appendChild(card(r)); });
          grid.innerHTML = '';
          grid.appendChild(frag);
          grid.scrollLeft = 0;
          section.setAttribute('data-reviews-live', String(out.length));
        })
        .catch(function () { /* keep the server-rendered cards */ });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
